import React from "react";
import Facebook from "@/images/v2/facebook-icon.svg";
import instagram from "@/images/v2/instagram-icon.svg";
import "./AgentBioAbout.scss";

export const embedUrl = (url) => {
  const value = String(url || "");
  const standard = value.match(/^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/)([\w-]{11})/);
  const short = value.match(/^(?:https?:\/\/)?youtu\.be\/([\w-]{11})/);
  const id = (standard && standard[1]) || (short && short[1]);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

// Agent-supplied links render on a public page: only http(s) may reach an href,
// and scheme-less values ("www.foo.com") get https:// instead of becoming relative links.
export const safeHref = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return null;
  try {
    const parsed = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    return ["http:", "https:"].includes(parsed.protocol) && parsed.hostname ? parsed.href : null;
  } catch (error) {
    return null;
  }
};

export const displayDomain = (url) =>
  String(url || "")
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");

const Globe = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const quotesOf = (agent) =>
  (Array.isArray(agent?.clientTestimonial) ? agent.clientTestimonial : []).filter((item) => String(item || "").trim());

const linksOf = (agent) =>
  [
    { key: "website", url: agent?.websiteUrl, label: displayDomain(agent?.websiteUrl), icon: <Globe /> },
    { key: "facebook", url: agent?.facebookUrl, label: "Facebook", icon: <img src={Facebook} alt="" /> },
    { key: "instagram", url: agent?.instagramUrl, label: "Instagram", icon: <img src={instagram} alt="" /> },
  ]
    .map(({ url, ...link }) => ({ ...link, href: safeHref(url) }))
    .filter((link) => link.href);

export const hasTestimonials = (agent) => quotesOf(agent).length > 0;
export const hasLinks = (agent) => linksOf(agent).length > 0 || Boolean(embedUrl(agent?.youtubeUrl));

export const AgentBioTestimonials = ({ agent }) => {
  const testimonials = quotesOf(agent);
  if (!testimonials.length) return null;
  const firstName = agent.FirstName || "this agent";
  const [featured, ...more] = testimonials;
  return (
    <section className="agent-bio-about">
      <h2 className="agent-bio-section-title">What clients say about {firstName}</h2>
      <figure className="agent-bio-about__featured">
        <span className="agent-bio-about__mark" aria-hidden="true">
          &ldquo;
        </span>
        <blockquote>{featured}</blockquote>
        <figcaption>Client of {firstName}</figcaption>
      </figure>
      {more.length ? (
        <div className="agent-bio-about__quotes">
          {more.map((quote, index) => (
            <figure key={index} className="agent-bio-about__quote">
              <blockquote>{quote}</blockquote>
              <figcaption>Client of {firstName}</figcaption>
            </figure>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const MISSING_SOCIAL = [
  { key: "facebook", field: "facebookUrl", label: "Add Facebook", icon: Facebook },
  { key: "instagram", field: "instagramUrl", label: "Add Instagram", icon: instagram },
];

export const withGitchaSource = (href) => {
  try {
    const url = new URL(href);
    url.searchParams.set("utm_source", "gitcha");
    url.searchParams.set("utm_medium", "bio");
    url.searchParams.set("utm_campaign", "agent_profile");
    return url.href;
  } catch {
    return href;
  }
};

const trackFor = (key) => (key === "website" ? { type: "website" } : { type: "social", link: key });

export const AgentBioLinks = ({ agent, showMissing = false, onTrack }) => {
  const links = linksOf(agent).map((link) =>
    onTrack && link.key === "website" ? { ...link, href: withGitchaSource(link.href) } : link,
  );
  const video = embedUrl(agent?.youtubeUrl);
  const missing = showMissing ? MISSING_SOCIAL.filter((item) => !agent?.[item.field]) : [];
  if (!links.length && !video && !missing.length) return null;
  const firstName = agent.FirstName || "this agent";
  return (
    <section className="agent-bio-about">
      <h2 className="agent-bio-section-title">Find {firstName} online</h2>
      {video ? (
        <div className="agent-bio-about__video">
          <iframe
            src={video}
            title={`${firstName} on YouTube`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ) : null}
      {links.length || missing.length ? (
        <div className="agent-bio-about__links">
          {links.map((link) => (
            <a
              key={link.key}
              className="agent-bio-about__link"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onTrack ? () => onTrack(trackFor(link.key)) : undefined}
            >
              {link.icon}
              <span>{link.label}</span>
            </a>
          ))}
          {missing.map((item) => (
            <span key={item.key} className="agent-bio-about__link agent-bio-about__link--missing">
              <img src={item.icon} alt="" />
              <span>{item.label}</span>
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
};
